import { useState } from 'react';
import {
  Calendar,
  Download,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  Package,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  Counter,
  Drawer,
  EmptyState,
  Field,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Progress,
  Radio,
  Select,
  Skeleton,
  SkeletonText,
  Spinner,
  StatCard,
  StatusBadge,
  Switch,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Tabs,
  Textarea,
} from '../components/ui';

/**
 * Vitrine da camada de componentes (Fase B).
 *
 * Serve pra revisar a anatomia do Atmos antes de migrar as telas reais
 * (Fase C) e como referência de uso enquanto a migração acontece.
 */
export default function DesignSystem() {
  const [tab, setTab] = useState('componentes');
  const [modal, setModal] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [switchOn, setSwitchOn] = useState(true);
  const [range, setRange] = useState('semana');
  const [page, setPage] = useState(1);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <PageHeader
        title="Design system"
        description="Atmos ERP — camada de componentes do painel"
        actions={
          <>
            <Button variant="secondary" leadingIcon={<Download size={15} />}>
              Exportar
            </Button>
            <Button leadingIcon={<Plus size={15} />}>Novo registro</Button>
          </>
        }
      />

      <Tabs
        className="mb-6"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'componentes', label: 'Componentes' },
          { value: 'tokens', label: 'Tokens' },
        ]}
      />

      {tab === 'tokens' ? <TokensTab /> : (
        <div className="flex flex-col gap-6">
          {/* Indicadores */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Agendamentos do mês"
              value="1.284"
              delta={{ value: '+8,4% vs. julho', direction: 'up' }}
            />
            <StatCard
              label="Executados"
              value="1.107"
              delta={{ value: '+3,1% vs. julho', direction: 'up' }}
            />
            <StatCard
              label="Cancelados"
              value="42"
              delta={{ value: '-1,2% vs. julho', direction: 'down' }}
            />
            <StatCard label="Vagas livres hoje" value="17" />
          </div>

          <Section title="Botões" spec="Button · IconButton · ButtonGroup">
            <div className="flex flex-wrap items-center gap-2.5">
              <Button>Primário</Button>
              <Button variant="secondary">Secundário</Button>
              <Button variant="outline">Contorno</Button>
              <Button variant="ghost">Fantasma</Button>
              <Button variant="danger" leadingIcon={<Trash2 size={15} />}>
                Excluir
              </Button>
              <IconButton label="Mais ações">
                <MoreHorizontal size={16} />
              </IconButton>
              <ButtonGroup
                value={range}
                onChange={setRange}
                options={[
                  { value: 'dia', label: 'Dia' },
                  { value: 'semana', label: 'Semana' },
                  { value: 'mes', label: 'Mês' },
                ]}
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2.5 border-t border-line-subtle pt-4">
              <Button variant="secondary" size="sm">
                Pequeno · 28
              </Button>
              <Button variant="secondary" size="md">
                Médio · 34
              </Button>
              <Button variant="secondary" size="lg">
                Grande · 40
              </Button>
              <span className="mx-1.5 h-6 w-px bg-line" />
              <Button loading>Salvando…</Button>
              <Button variant="secondary" disabled>
                Desabilitado
              </Button>
            </div>
          </Section>

          <Section title="Campos" spec="Field · Input · Select · Textarea">
            <div className="grid gap-[18px_20px] sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Nome do cliente" required hint="Como consta no contrato.">
                <Input defaultValue="Metalix Telecom LTDA" />
              </Field>
              <Field label="CPF / CNPJ" error="Documento incompleto — faltam 3 dígitos.">
                <Input defaultValue="12.845.902/0001-" invalid className="font-mono" />
              </Field>
              <Field label="Valor da mensalidade" hint="Alinhado à direita, dígitos tabulares.">
                <Input defaultValue="129,90" prefix="R$" numeric />
              </Field>
              <Field label="Período">
                <Select defaultValue="manha">
                  <option value="manha">Manhã</option>
                  <option value="tarde">Tarde</option>
                </Select>
              </Field>
              <Field label="Data do agendamento">
                <Input
                  defaultValue="14/08/2026"
                  className="font-mono"
                  trailingIcon={<Calendar size={15} />}
                />
              </Field>
              <Field label="Buscar" hint="Debounce de 350ms.">
                <Input
                  placeholder="Cliente, contrato, bairro ou cidade"
                  leadingIcon={<Search size={15} />}
                />
              </Field>
              <Field label="Observação da agenda" className="sm:col-span-2">
                <Textarea defaultValue="Ir antes das 14h — cliente sai para o trabalho." />
              </Field>
              <div className="flex flex-col gap-2.5">
                <span className="text-[12.5px] font-medium text-fg-2">Opções</span>
                <Checkbox label="Consome vaga na agenda" defaultChecked />
                <Radio name="ds-demo" label="Notificar o vendedor" defaultChecked />
                <Radio name="ds-demo" label="Não notificar" />
                <Switch checked={switchOn} onChange={setSwitchOn} label="Enviar push ao cliente" />
              </div>
            </div>
          </Section>

          <Section title="Status" spec="badge = ícone + cor + rótulo">
            <div className="flex flex-wrap gap-2">
              <StatusBadge tone="neutral">Agendado</StatusBadge>
              <StatusBadge tone="primary">Em rota</StatusBadge>
              <StatusBadge tone="success">Executado</StatusBadge>
              <StatusBadge tone="warning">Pendente</StatusBadge>
              <StatusBadge tone="danger">Cancelado</StatusBadge>
              <StatusBadge tone="info">Reagendado</StatusBadge>
              <Badge tone="neutral">Sem ícone</Badge>
              <Counter>12</Counter>
            </div>
            <p className="mt-4 max-w-2xl text-[13px] text-fg-2">
              A cor nunca carrega o significado sozinha: o ícone e o rótulo estão sempre
              presentes, então o status continua legível para quem tem daltonismo e em
              relatório impresso.
            </p>
          </Section>

          <Section title="Feedback" spec="Alert · Dialog · Drawer · Progress">
            <div className="grid gap-5 lg:grid-cols-2">
              <div className="flex flex-col gap-3">
                <Alert tone="danger" title="Falha ao sincronizar com o ERP">
                  O endpoint /api/ura/clientes respondeu 500. Os planos exibidos podem estar
                  desatualizados.
                </Alert>
                <Alert tone="warning" title="12 agendamentos sem técnico">
                  Distribua as ordens antes das 18h para o roteiro de amanhã.
                </Alert>
                <Alert tone="info" title="Manutenção programada">
                  A OLT SP-01 ficará indisponível em 30/08, às 22:00.
                </Alert>
                <Alert
                  tone="neutral"
                  action={
                    <Button variant="secondary" size="sm">
                      Revisar
                    </Button>
                  }
                >
                  3 usuários ainda não ativaram a verificação em duas etapas.
                </Alert>
              </div>
              <div className="flex flex-col gap-5">
                <Card>
                  <CardHeader title="Sobreposições" description="Cada uma tem um uso próprio." />
                  <CardBody className="flex flex-wrap gap-2.5">
                    <Button variant="secondary" size="sm" onClick={() => setModal(true)}>
                      Modal
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setDialog(true)}>
                      Confirmação
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setDrawer(true)}>
                      Drawer
                    </Button>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="flex flex-col gap-4">
                    <Progress value={68} label="Importando agendamentos" />
                    <div className="flex items-center gap-2.5 text-[13px] text-fg-2">
                      <Spinner /> Consultando o ERP…
                    </div>
                    <SkeletonText lines={3} />
                  </CardBody>
                </Card>
              </div>
            </div>
          </Section>

          <Section title="Tabela" spec="Table · Pagination">
            <Table>
              <THead>
                <TR>
                  <TH>Cliente</TH>
                  <TH>Contrato</TH>
                  <TH>Cidade</TH>
                  <TH>Status</TH>
                  <TH align="right">Valor</TH>
                </TR>
              </THead>
              <TBody>
                {DEMO_ROWS.map((r) => (
                  <TR key={r.contrato} interactive>
                    <TD className="font-medium text-fg">{r.cliente}</TD>
                    <TD numeric>{r.contrato}</TD>
                    <TD>{r.cidade}</TD>
                    <TD>
                      <StatusBadge tone={r.tone}>{r.status}</StatusBadge>
                    </TD>
                    <TD align="right" numeric>
                      {r.valor}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <Pagination
              className="mt-4"
              page={page}
              pageCount={107}
              total={1284}
              pageSize={12}
              onChange={setPage}
            />
          </Section>

          <Section title="Contêineres" spec="Card · EmptyState">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Card className="p-4">
                <p className="text-xs font-medium text-muted">Card base</p>
                <p className="mt-1.5 text-[13px] text-fg-2">
                  Borda, raio 12 e sombra sutil. Nunca aninhado em outro card.
                </p>
              </Card>
              <Card interactive className="p-4">
                <p className="text-xs font-medium text-muted">Card interativo</p>
                <p className="mt-1.5 text-[13px] text-fg-2">
                  O hover sobe um degrau de elevação. O card inteiro é a área de clique.
                </p>
              </Card>
              <EmptyState
                icon={<Package size={20} />}
                title="Nenhum agendamento"
                description="Nenhuma ordem para os filtros selecionados."
                action={
                  <Button size="sm" leadingIcon={<Plus size={14} />}>
                    Novo agendamento
                  </Button>
                }
              />
            </div>
          </Section>
        </div>
      )}

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        onConfirm={() => setModal(false)}
        title="Novo agendamento"
        description="O Enter confirma; o clique fora não fecha."
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(false)}>
              Cancelar
            </Button>
            <Button onClick={() => setModal(false)}>Salvar</Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Cliente" required>
            <Input placeholder="Nome ou CPF/CNPJ" leadingIcon={<Search size={15} />} />
          </Field>
          <Field label="Período">
            <Select defaultValue="manha">
              <option value="manha">Manhã</option>
              <option value="tarde">Tarde</option>
            </Select>
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={dialog}
        onClose={() => setDialog(false)}
        onConfirm={() => setDialog(false)}
        icon={<Trash2 size={17} />}
        title="Cancelar 3 agendamentos?"
        description="As ordens já abertas no ERP serão canceladas junto. A ação fica registrada no histórico."
        confirmLabel="Cancelar ordens"
        cancelLabel="Voltar"
      />

      <Drawer
        open={drawer}
        onClose={() => setDrawer(false)}
        title="Metalix Telecom LTDA"
        description="Contrato 184203 · Sorocaba"
        footer={
          <Button variant="secondary" onClick={() => setDrawer(false)}>
            Fechar
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <Alert tone="info">
            O drawer serve para inspecionar um registro sem sair da lista.
          </Alert>
          <SkeletonText lines={5} />
        </div>
      </Drawer>
    </div>
  );
}

function Section({
  title,
  spec,
  children,
}: {
  title: string;
  spec: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between gap-4 border-b border-line pb-2.5">
        <h2 className="text-[17px] font-semibold tracking-[-0.02em] text-fg">{title}</h2>
        <span className="font-mono text-xs text-muted">{spec}</span>
      </div>
      <div className="rounded-xl border border-line bg-surface p-5 shadow-xs">{children}</div>
    </section>
  );
}

function TokensTab() {
  return (
    <div className="flex flex-col gap-6">
      <Section title="Cor" spec="tokens semânticos">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {TOKEN_GROUPS.map((g) => (
            <div key={g.label}>
              <p className="mb-2 text-xs font-medium text-muted">{g.label}</p>
              <div className="flex flex-col gap-1.5">
                {g.tokens.map((t) => (
                  <div key={t} className="flex items-center gap-2.5">
                    <span
                      className="h-7 w-7 flex-none rounded-md border border-line"
                      style={{ background: `var(--${t})` }}
                    />
                    <code className="font-mono text-[11.5px] text-fg-2">--{t}</code>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Tipografia" spec="Geist · Geist Mono">
        <div className="flex flex-col divide-y divide-line-subtle">
          {TYPE_SCALE.map((t) => (
            <div key={t.token} className="grid grid-cols-[110px_1fr] items-center gap-5 py-3.5">
              <code className="font-mono text-xs text-primary">{t.token}</code>
              <span className={t.className}>{t.sample}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Elevação" spec="surface + shadow, nunca a sombra sozinha">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { name: 'subtle', cls: 'shadow-xs', use: 'cards, inputs, botões' },
            { name: 'medium', cls: 'shadow-md', use: 'dropdown, popover' },
            { name: 'large', cls: 'shadow-lg', use: 'toast, drawer' },
            { name: 'modal', cls: 'shadow-xl', use: 'modal sobre o scrim' },
          ].map((s) => (
            <div key={s.name}>
              <div className={`h-20 rounded-xl border border-line-subtle bg-surface ${s.cls}`} />
              <p className="mt-2.5 text-[12.5px] font-semibold text-fg">{s.name}</p>
              <p className="text-[11.5px] text-muted">{s.use}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Raio" spec="5 · 8 · 12 · 16 · pill">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[
            { name: 'sm · 5', cls: 'rounded-sm' },
            { name: 'md · 8', cls: 'rounded-lg' },
            { name: 'lg · 12', cls: 'rounded-xl' },
            { name: 'xl · 16', cls: 'rounded-2xl' },
            { name: 'pill', cls: 'rounded-full' },
          ].map((r) => (
            <div key={r.name} className="text-center">
              <div className={`h-16 border border-line bg-neutral-soft ${r.cls}`} />
              <p className="mt-2 text-xs font-medium text-fg-2">{r.name}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

const TOKEN_GROUPS = [
  { label: 'Superfície', tokens: ['bg', 'surface', 'surface-2', 'sunken', 'hover', 'active'] },
  { label: 'Traço e texto', tokens: ['border', 'border-subtle', 'text', 'text-2', 'muted', 'disabled'] },
  { label: 'Primária', tokens: ['primary', 'primary-hover', 'primary-soft', 'primary-border', 'primary-solid'] },
  { label: 'Semânticas', tokens: ['success', 'warning', 'danger', 'info', 'neutral-soft'] },
];

const TYPE_SCALE = [
  { token: 'display', className: 'text-[34px] font-semibold tracking-[-0.035em] text-fg', sample: 'Controle operacional' },
  { token: 'h1', className: 'text-[26px] font-semibold tracking-[-0.03em] text-fg', sample: 'Agenda de instalações' },
  { token: 'h2', className: 'text-[19px] font-semibold tracking-[-0.022em] text-fg', sample: 'Resumo do contrato' },
  { token: 'h3', className: 'text-[15px] font-semibold tracking-[-0.015em] text-fg', sample: 'Condições comerciais' },
  { token: 'body', className: 'text-[14px] text-fg', sample: 'O agendamento 118420 foi transmitido ao ERP e confirmado.' },
  { token: 'label', className: 'text-[12.5px] font-medium text-fg-2', sample: 'Documento do cliente (CPF)' },
  { token: 'caption', className: 'text-[11.5px] text-muted', sample: 'Atualizado há 4 minutos por D. Cardoso' },
  { token: 'code', className: 'font-mono text-[12.5px] text-fg', sample: 'R$ 184.320,00 · PED-20841' },
];

const DEMO_ROWS = [
  { cliente: 'Metalix Telecom', contrato: '184203', cidade: 'Sorocaba', status: 'Executado', tone: 'success' as const, valor: 'R$ 129,90' },
  { cliente: 'Ana Duarte', contrato: '184591', cidade: 'Votorantim', status: 'Agendado', tone: 'neutral' as const, valor: 'R$ 99,90' },
  { cliente: 'Northwind Ind.', contrato: '183776', cidade: 'Sorocaba', status: 'Pendente', tone: 'warning' as const, valor: 'R$ 349,90' },
  { cliente: 'Carlos Menezes', contrato: '185012', cidade: 'Araçoiaba', status: 'Cancelado', tone: 'danger' as const, valor: 'R$ 129,90' },
];
